import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './Chart.scss';
import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts';

const Chart = ({ className, data, style }) => {
  const chartRef = useRef();

  const [activeIndex, setActiveIndex] = useState(null);

  const onMouseLeave = () => {
    setActiveIndex(null);
  };

  const onMouseEnter = (_, index) => {
    setActiveIndex(index);
  };

  const shouldBeOpaque = (index) => {
    return activeIndex === null || activeIndex === index;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const customTooltip = ({ active, _label, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p>{payload[0].name}</p>
        </div>
      );
    }
    return null;
  };

  const transformData = (inData) => {
    const rechartsData = [];
    inData.categories.forEach((category, index) => {
      const rechartsDatum = {};
      if (typeof category === 'string') {
        rechartsDatum.name = category;
        rechartsDatum.percent = data.series[0].data[index];
        rechartsData.push(rechartsDatum);
        return;
      }
      rechartsDatum.name = category.label;
      rechartsDatum.fillColor = category.color;
      rechartsDatum.percent = data.series[0].data[index];
      rechartsData.push(rechartsDatum);
    });
    return rechartsData;
  };

  const rechartsData = transformData(data);

  return (
    <div
      className={classnames('er-chart', className)}
      style={style}
      ref={chartRef}
    >
      <PieChart className="pie-chart" width={360} height={180}>
        <Tooltip content={customTooltip} />
        <Legend
          width={150}
          verticalAlign="top"
          align="right"
          layout="vertical"
          className="reflect-charts-legend-right"
          formatter={(value, entry, index) => {
            return (
              <span
                onMouseEnter={() => onMouseEnter(null, index)}
                onMouseLeave={onMouseLeave}
                aria-current={activeIndex === index}
                className={classnames(
                  shouldBeOpaque(index)
                    ? 'legend-item'
                    : 'legend-item-hover-excluded',
                )}
              >
                {value}
              </span>
            );
          }}
        />
        <Pie
          data={rechartsData}
          dataKey="percent"
          nameKey="name"
          cx={95}
          innerRadius={55}
          outerRadius={75}
          endAngle={360 + 90}
          startAngle={90}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {rechartsData.map((entry, index) => {
            return (
              <Cell
                key={entry.name}
                fill={entry.fillColor}
                aria-current={activeIndex === index}
                className={classnames(
                  shouldBeOpaque(index)
                    ? 'pie-slice'
                    : 'pie-slice-hover-excluded',
                )}
              />
            );
          })}
        </Pie>
      </PieChart>
    </div>
  );
};

Chart.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    categories: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    ),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
      }),
    ),
  }).isRequired,
  style: PropTypes.shape({
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

Chart.defaultProps = {
  className: '',
  style: {},
};

export default Chart;
